import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { __ } from '../../translations';
import url from '../../helpers/url';
import { screenDetailTypes } from '../../config/constants';
//@nx/enforce-module-boundaries
import { Modal } from '../ui/Modal';
import { Button, FlexWrapper, Text } from '@intigral/shared';

interface ConfirmPopupProps {
    title?: string | null;
    description?: string;
    cancelButtonText?: string;
    doneButtonText?: string;
    autoRedirect?: boolean;
    popupAction?: boolean;
    showPopup?: boolean;
    onClose?: (value: boolean) => void;
}

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
    title,
    description,
    cancelButtonText,
    doneButtonText,
    autoRedirect = false,
    popupAction = true,
    showPopup = false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onClose = () => {},
}) => {
    const popupRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!popupRef.current) {
            return;
        }
        // disable body scroll on show popup
        if (showPopup) {
            disableBodyScroll(popupRef.current);
            if (autoRedirect) {
                setTimeout(() => {
                    navigate(url.route(url.getDetailsPage(screenDetailTypes.PROFILE_SELECT)));
                }, 4000);
            }
        } else {
            enableBodyScroll(popupRef.current);
            clearAllBodyScrollLocks();
        }
    }, [showPopup]);

    const closePopupHandler = (
        e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement> | false = false,
        redirect = false
    ) => {
        if (e && !redirect) e.preventDefault();
        onClose(false);
    };

    const submitHandler = () => {
        onClose(true);
        return true;
    };

    return (
        <Modal
            useFlexLayout={false}
            open={showPopup}
            onOpenChange={() => {
                closePopupHandler();
            }}
            closePlacement="null"
            widthSet="medium"
        >
            <FlexWrapper flexDirection="column" gap="4x">
                <Text as="h2" variant="h2" margin="0" alignment="center">
                    {title || __('confirm_popup_title')} {}
                </Text>
                <Text as="span" color="gray" size={{ default: '16', md: '20' }}>
                    {description || __('confirm_popup_description')}
                </Text>
                {!!popupAction && (
                    <FlexWrapper direction={{ default: 'column', md: 'row' }} gap={{ default: '2x', md: '4x' }}>
                        <Button onClick={submitHandler} fullWidth>
                            {doneButtonText || __('confirm_popup_yes_button')}
                        </Button>
                        <Button inverse onClick={closePopupHandler} fullWidth>
                            {cancelButtonText || __('confirm_popup_no_button')}
                        </Button>
                    </FlexWrapper>
                )}
            </FlexWrapper>
        </Modal>
    );
};

export default ConfirmPopup;
